import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory, generatePath } from 'react-router-dom';

import Text from '../../containers/Text';
import AppLogo from '../../shared/icons/logo2.png';
import BackArrow from '../../shared/icons/back-arrow-icon.svg';

import { AuthService } from '../../services/auth';

import { SignForm } from './SignForm';

import { SessionStorageService } from '../../shared/services/session-storage';
import { getErrorMessage } from '../../shared/services/error-handler';
import '../../languages/i18n';
import { Routes } from '../../api/routes';

import './Auth.scss';
import { OTPGetCode } from './OTPGetCode';
import { generateOTP, signIn } from '../../api/requests/auth';
import { Mail, Phone } from 'lucide-react';


const Auth = ({ setUserInfo, currentLng, changeLanguage, setLanguage }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const [error, setError] = useState({ active: false, key: null });
  const [email, setEmail] = useState(location?.search?.split('=')[1] || '');
  const [code, setCode] = useState('');
  const [identityNumber, setIdentityNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const setNewLanguage = (lng) => {
    changeLanguage(lng);
    SessionStorageService.setAppLanguage(lng);
    setLanguage({ language: lng });
  };

  const validateGenerateOtpForm = () => {
    if (!email || !identityNumber) return 'fill-all-fields';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'invalid-email';

    const idRegex = /^\d{7,9}$/;
    if (!idRegex.test(identityNumber.trim())) return 'invalid-id-number';

    return null;
  };


  const handleGenerateOtpClick = async () => {
    try {
      setIsLoading(true);

      await generateOTP({
        email: email.trim(),
        identityNumber: Number(identityNumber),
      });

    } catch (error) {
      setError({ active: true, key: 'something-went-wrong' });
    } finally {
      setIsLoading(false);

      // alwyas navigate to next page
      history.push(
        generatePath(`${Routes.oneTimePassword}?email=${email}`)
      );
    }
  };

  const handleSignInClick = async () => {
    try {
      setIsLoading(true);

      const response = await signIn({
        email: email,
        otpCode: code,
      });

      AuthService.setToken(response);
      const decodedToken = AuthService.getTokenData();
      setUserInfo({ ...decodedToken });
    } catch (error) {
      const key = 'invalid-otp-code'
      setError({ active: true, key });
      // handleError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    history.push(Routes.signIn);
  };

  // const handleError = (key) => {
  //   if (key) setError({ active: true, key });
  // };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear previous errors
    setError({ active: false, message: '' });

    if (location.pathname === Routes.signIn) {

      const validationKey = validateGenerateOtpForm();
      if (validationKey) {
        setError({ active: true, key: validationKey });
        return; // Do not proceed to the server
      }

      await handleGenerateOtpClick();
      return;
    }
    await handleSignInClick();
  };

  return (
    <div className="auth-container">
      {/* 1 */}
      <div className="auth-lng-change-wrapper">
        <div className="lng-change-container">
          {currentLng === 'heb' ? (
            <>
              <button
                onClick={() => setNewLanguage('heb')}
                className={`lng-button ${currentLng === 'heb' ? 'active' : ''}`}
              >
                <Text text={t('heb-lang-button')} />
              </button>
              <button
                onClick={() => setNewLanguage('en')}
                className={`lng-button ${currentLng === 'en' ? 'active' : ''}`}
              >
                <Text text={t('eng-lang-button')} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setNewLanguage('en')}
                className={`lng-button ${currentLng === 'en' ? 'active' : ''}`}
              >
                <Text text={t('eng-lang-button')} />
              </button>
              <button
                onClick={() => setNewLanguage('heb')}
                className={`lng-button ${currentLng === 'heb' ? 'active' : ''}`}
              >
                <Text text={t('heb-lang-button')} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* 2 */}
      <div className="auth-wrapper">
        <form onSubmit={handleFormSubmit}>
          <div className="form-head">
            <Text
              text={<img src={AppLogo} className="app-logo" alt="app logo" />}
            />
          </div>

          <div className={'login-title'}>
            <Text text={t('login-title')} />
          </div>

          <div className={'fields-container'}>
            {location.pathname === Routes.oneTimePassword ? (
              <OTPGetCode
                email={email}
                code={code}
                setCode={setCode}
                error={error}
                isLoading={isLoading}
                handleBack={handleGoBack}
              />
            ) : (
              <SignForm
                email={email}
                setEmail={setEmail}
                error={error}
                identityNumber={identityNumber}
                setIdentityNumber={setIdentityNumber}
                isLoading={isLoading}
              />
            )}
          </div>
        </form>
      </div>

      {/* 3 */}
      <div className="bottom">
        <div className="contact-container login-subtitle">
          <p className="contact-us">{t('login-subtitle')}</p>
        </div>
        <div className="contact-container">
          <Mail className="contact-icon" />
          <p className="contact-mail">
            <a
              href="mailto:info@yeuls.co.il"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Text text="info@yeuls.co.il" />
            </a>
          </p>
        </div>

        <div className='contact-container'>
          <Phone className='contact-icon' />
          <p className="contact number">
            <Text text={'\u202A(+972) 54-261-8904\u202C'} />
          </p>
        </div>
      </div>
    </div>
  );
};

export { Auth };
